import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { CSVLink } from 'react-csv';
import { withRouter } from 'react-router';

import Divider from '@material-ui/core/Divider';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import Modal from '@material-ui/core/Modal';
import Tooltip from '@material-ui/core/Tooltip';
import { makeStyles } from '@material-ui/core/styles';
import { Check, ListAlt, Remove } from '@material-ui/icons';

import EntityListFrame from 'src/components/layouts/EntityListFrame';
import LandDeliveryModal from 'src/components/pages/LandDelivery/LandDeliveryModal';
import ColumnSelector from 'src/components/parts/ColumnSelector';
import DataContentTable from 'src/components/parts/DataContentTable';
import DataTableFilter from 'src/components/parts/DataTableFilter/DataTableFilter';
import FeatureDisabledMessage from 'src/components/parts/FeatureDisabledMessage';
import useCatchAPIError from 'src/hooks/useCatchAPIError';
import useCustomerAccountFeatures from 'src/hooks/useCustomerAccountFeatures';
import useGlobalLoadingBar from 'src/hooks/useGlobalLoadingBar';
import dateBaseOn from 'src/models/DateBaseOn';
import apiService from 'src/services/api.service';
import csvTableService from 'src/services/csv.table.service';
import pdfTableService from 'src/services/pdf.table.service';
import ExportMenu from 'src/wrappers/ExportMenu';

const useStyles = makeStyles((theme) => ({
  titleArea: {
    marginBottom: '28px',
    paddingLeft: theme.spacing(1.5),
    paddingRight: theme.spacing(1.5),
  },
  overviewIcon: {
    textAlign: 'center',
  },
  modalContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalContentContainer: {
    backgroundColor: theme.palette.background.paper,
    border: '2px solid #000',
    boxShadow: theme.shadows[5],
    padding: theme.spacing(2, 4, 3),
    width: '60vw',
    height: '60vh',
    overflow: 'scroll',
  },
  editButton: {
    color: theme.palette.primaryButton.backgroundColor,
  },
}));

const defaultHeadCells = [
  {
    id: 'Urgent',
    numeric: false,
    disablePadding: false,
    label: 'Urgent',
    align: 'center',
  },
  {
    id: 'ContainerNumber',
    numeric: false,
    disablePadding: false,
    label: 'Container No.',
  },
  {
    id: 'ShipmentNumber',
    numeric: false,
    disablePadding: false,
    label: 'Shipment No.',
  },
  {
    id: 'VesselName',
    numeric: false,
    disablePadding: false,
    label: 'Vessel',
  },
  {
    id: 'ETD',
    numeric: false,
    disablePadding: false,
    label: 'ETD',
  },
  {
    id: 'ETA',
    numeric: false,
    disablePadding: false,
    label: 'ETA',
  },
  {
    id: 'Destination',
    numeric: false,
    disablePadding: false,
    label: 'Destination',
  },
  {
    id: 'FirstDayAvailabilityDate',
    numeric: false,
    disablePadding: false,
    label: 'First Day Availability',
  },
  {
    id: 'StorageStartDate',
    numeric: false,
    disablePadding: false,
    label: 'Storage Dates',
  },
  {
    id: 'ContainerDehireDate',
    numeric: false,
    disablePadding: false,
    label: 'Container De-hire',
  },
  {
    id: 'ActualDeliveryDate',
    numeric: false,
    disablePadding: false,
    label: 'Actual Delivery',
  },
  {
    id: 'ClientRequestedDeliveryDate',
    numeric: false,
    disablePadding: false,
    label: 'Requested Delivery Date',
  },
  {
    id: 'EmptyReady',
    numeric: false,
    disablePadding: false,
    label: 'Empty Ready',
    align: 'center',
  },
];

function LandDeliveryTable(props) {
  const classes = useStyles();
  const [order, setOrder] = React.useState('desc');
  const [orderBy, setOrderBy] = React.useState('ShipmentNumber');
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(25);
  const [rows, setRows] = React.useState('');
  const [filter, setFilter] = React.useState();
  const [totalNumberOfRows, setTotalNumberOfRows] = React.useState(0);
  const [downloadCsv, setDownloadCsv] = React.useState({ headers: [], data: [], filename: '' });
  const csvLinkEl = useRef();
  const [baseQueryString, setBaseQueryString] = React.useState();
  const [modalOpen, setModalOpen] = React.useState(false);
  const [selectedShipmentNumber, setSelectedShipmentNumber] = React.useState();
  const [selectedContainerNumber, setSelectedContainerNumber] = React.useState();

  const { enabledLandDeliveryFeature } = useCustomerAccountFeatures();
  const { catchApiError } = useCatchAPIError();
  const { startProgress, stopProgress } = useGlobalLoadingBar();

  const [headCells, setHeadCells] = useState(defaultHeadCells);
  const [headCellsSelect, setHeadCellsSelect] = useState();

  useEffect(() => {
    if (filter) {
      startProgress();
      getData();
    }
  }, [rowsPerPage, page, filter]);

  const filterCallback = (data) => {
    setPage(0);
    setFilter(data);
  };

  const getData = (inData) => {
    let data = inData !== undefined ? inData : filter;
    apiService
      .getLandDeliveryTable(
        data.accountId,
        data.dateBasedOn,
        data.start,
        data.end,
        order,
        orderBy,
        page * rowsPerPage,
        rowsPerPage,
        data.freeTextSearch,
        data.suppliers
      )
      .then((result) => {
        setRows(createTableListData(result.data.items));
        setTotalNumberOfRows(result.data.count);
        stopProgress();
      })
      .catch(catchApiError);
  };

  const createTableListData = (items) => {
    let newArray = [];
    var otherQueryString = baseQueryString;
    if (otherQueryString) {
      otherQueryString = '&' + otherQueryString;
    }

    items.map((item, index) => {
      let etd = moment(item.etd);
      let eta = moment(item.eta);
      let firstDayAvailabilityDate = moment(item.firstDayAvailabilityDate);
      let storageStartDate = moment(item.storageStartDate);
      let containerDehireDate = moment(item.containerDehireDate);
      let actualDeliveryDate = moment(item.actualDeliveryDate);
      let clientRequestedDeliveryDate = moment(item.clientRequestedDeliveryDate);

      var shipmentNumberLinks = {
        component: 'linkList',
        links: [
          {
            text: item.shipmentNumber,
            path: '/trackandtrace?s=' + item.shipmentNumber,
          },
        ],
      };

      let rowData = {
        id: item.shipmentNumber + '_' + item.containerNumber,
        isUrgent: item.urgent, // Hidden field for logic
        Urgent: item.urgent ? <Check style={{ color: 'black' }} /> : <Remove style={{ color: 'grey' }} />,
        ContainerNumber: item.containerNumber,
        ShipmentNumber: shipmentNumberLinks,
        VesselName: item.vesselName,
        ETD: etd.isValid() ? etd.format('L') : '',
        ETA: eta.isValid() ? eta.format('L') : '',
        Destination: item.destination,
        FirstDayAvailabilityDate: firstDayAvailabilityDate.isValid() ? firstDayAvailabilityDate.format('L') : '',
        StorageStartDate: storageStartDate.isValid() ? storageStartDate.format('L') : '',
        ContainerDehireDate: containerDehireDate.isValid() ? containerDehireDate.format('L') : '',
        ActualDeliveryDate: actualDeliveryDate.isValid() ? actualDeliveryDate.format('L') : '',
        ClientRequestedDeliveryDate: clientRequestedDeliveryDate.isValid() ? clientRequestedDeliveryDate.format('L LT') : '',
        EmptyReady: item.emptyReady ? <Check style={{ color: 'black' }} /> : <Remove style={{ color: 'grey' }} />,
      };

      newArray.push(rowData);
    });
    setHeadCells(defaultHeadCells);
    return newArray;
  };

  const handleEditClick = (event) => {
    let id = event.currentTarget.parentNode.dataset.tag;
    if (id) {
      let shipmentNumber = id.split('_')[0];
      let containerNumber = id.split('_')[1];
      setSelectedShipmentNumber(shipmentNumber);
      setSelectedContainerNumber(containerNumber);
      setModalOpen(true);
    }
  };

  const handleModalClose = () => {
    setModalOpen(false);
    getData();
  };

  const handleRequestSort = (sortOrder, property) => {
    setOrder(sortOrder);
    setOrderBy(property);
    getData();
  };

  const handleChangePage = (newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (newValue) => {
    setPage(0);
    setRowsPerPage(newValue);
  };
  const generatePdf = () => {
    if (totalNumberOfRows > 0) {
      apiService
        .getLandDeliveryTable(
          filter.accountId,
          filter.dateBasedOn,
          filter.start,
          filter.end,
          order,
          orderBy,
          0,
          totalNumberOfRows,
          filter.freeTextSearch,
          filter.suppliers
        )
        .then((result) => {
          stopProgress();
          pdfTableService.generateLandDeliveriesPDF(
            result.data.items,
            'Land Delivery     Company:' + filter.accountName + '    Period:' + filter.periodName,
            'Land Delivery Report'
          );
        })
        .catch(catchApiError);
    }
  };

  const generateCSV = () => {
    if (totalNumberOfRows > 0) {
      apiService
        .getLandDeliveryTable(
          filter.accountId,
          filter.dateBasedOn,
          filter.start,
          filter.end,
          order,
          orderBy,
          0,
          totalNumberOfRows,
          filter.freeTextSearch,
          filter.suppliers
        )
        .then((result) => {
          stopProgress();
          var csvData = csvTableService.generateLandDeliveriesCsvData(result.data.items, filter.accountName + '_' + filter.periodName);
          setDownloadCsv(csvData);
          csvLinkEl.current.link.click();
        })
        .catch(catchApiError);
    }
  };

  const handleColumnSelected = (headCells) => {
    setHeadCellsSelect(headCells);
  };

  return (
    <>
      <CSVLink headers={downloadCsv.headers} filename={downloadCsv.filename} data={downloadCsv.data} ref={csvLinkEl} />
      <EntityListFrame>
        {enabledLandDeliveryFeature ? (
          <>
            <Grid container className={classes.titleArea}>
              <Grid item xs={12} sm className="align-self-center">
                <h4>Land Delivery</h4>
              </Grid>
              <Grid item className={'align-self-center '}>
                <ColumnSelector headCells={headCells} handleColumnSelected={handleColumnSelected} />
              </Grid>
              <Grid item className={'align-self-center '}>
                <ExportMenu onPdfClick={generatePdf} onCsvClick={generateCSV}></ExportMenu>
              </Grid>
            </Grid>
            <Grid item xs={12} sm={12} md={12} lg={12}>
              <DataTableFilter
                sendFilterComponentData={filterCallback}
                sendQueryStringsNoFreeTextSearch={setBaseQueryString}
                showSuppliers={false}
                dateBaseOns={[dateBaseOn.eta, dateBaseOn.etd]}
                showPeriodCalendar={true}
              />
            </Grid>
            <Divider />

            <Grid item xs={12}>
              {rows && (
                <DataContentTable
                  title=""
                  headCells={headCellsSelect}
                  totalNumberOfRows={totalNumberOfRows}
                  rows={rows}
                  page={page}
                  handleRequestSort={handleRequestSort}
                  handleChangePage={handleChangePage}
                  handleChangeRowsPerPage={handleChangeRowsPerPage}
                  rowStyle={(row) => ({ backgroundColor: row.isUrgent ? '#FFF4E5' : 'inherit' })}
                  rowActionComponent={
                    <Tooltip title="Manage Delivery Request" placement="center">
                      <IconButton className={classes.editButton} aria-label="edit" onClick={handleEditClick}>
                        <ListAlt size="small" className={classes.overviewIcon} />
                      </IconButton>
                    </Tooltip>
                  }
                />
              )}
            </Grid>
          </>
        ) : (
          <FeatureDisabledMessage />
        )}
      </EntityListFrame>
      <Modal className={classes.modalContainer} open={modalOpen} onClose={handleModalClose}>
        <LandDeliveryModal open={modalOpen} onClose={handleModalClose} shipmentNumber={selectedShipmentNumber} containerNumber={selectedContainerNumber} />
      </Modal>
    </>
  );
}

export default withRouter(LandDeliveryTable);
